import { Box, Typography } from "@mui/material";
import { type FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { SystemValidationTextField } from "../../../components/UIToolkit/Inputs/SystemValidationTextField";
import SystemDialog from "../../../components/UIToolkit/Popups/Dialog";
import { FONT_SIZE, INK, LINE_HEIGHT } from "../../../theme/tokens";
import { PRESET_NAME_ERROR_KEYS, validatePresetName } from "../printoutPresets";
import { CUSTOM_PRINTOUT_NAME_MAX_LENGTH } from "./customPrintoutTypes";

/**
 * The input's own cap, in UTF-16 units (an emoji takes two), only so a paste can't run away; the 40-character
 * limit itself is counted by code point in `validatePresetName`, the way the server counts.
 */
const NAME_INPUT_MAX_UTF16_UNITS = CUSTOM_PRINTOUT_NAME_MAX_LENGTH * 2;

type Props = {
	open: boolean;
	/** `create` names a new printout as it is saved; `rename` changes a saved one's name. */
	mode: "create" | "rename";
	/** The name the field starts with. */
	initialName: string;
	/** Every other printout's name — two can't share one. */
	otherNames: readonly string[];
	isSaving: boolean;
	onConfirm: (name: string) => void;
	onClose: () => void;
};

/**
 * Names a custom printout: when a new one is first saved, and on Rename. The name is checked as it's typed —
 * empty, too long, a bare date or already taken — the same rules the Detailed printout's saved printouts use.
 * Mount it per opening (`key`) so the field starts from `initialName` each time.
 */
export const CustomPrintoutNameDialog: FC<Props> = ({ open, mode, initialName, otherNames, isSaving, onConfirm, onClose }) => {
	const { t } = useTranslation();
	const [name, setName] = useState<string>(initialName);
	// Custom printouts have no fixed "Printout 1" whose label a name could clash with.
	const error = validatePresetName(name, otherNames, null);
	const confirm = (): void => {
		if (error === null && !isSaving) onConfirm(name.trim());
	};
	return (
		<SystemDialog
			open={open}
			onClose={onClose}
			title={t(mode === "create" ? "printout.custom.ai.name.dialog.create.title" : "printout.custom.ai.name.dialog.rename.title")}
			body={
				<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 0.5 }}>
					{mode === "create" && (
						<Typography sx={{ fontSize: FONT_SIZE.SECONDARY, lineHeight: LINE_HEIGHT.SNUG, color: INK.SECONDARY }}>
							{t("printout.custom.ai.name.dialog.create.body")}
						</Typography>
					)}
					<SystemValidationTextField
						dataTestId="custom-printout-name-input"
						label={t("printout.preset.name.label")}
						value={name}
						autoFocus
						fullWidth
						onChange={(event) => setName(event.target.value)}
						onKeyDown={(event) => {
							if (event.key === "Enter") confirm();
						}}
						injectedProps={{ slotProps: { htmlInput: { maxLength: NAME_INPUT_MAX_UTF16_UNITS } } }}
						validation={{
							validationCallback: () => error === null,
							helperText: { invalid: error === null ? undefined : t(PRESET_NAME_ERROR_KEYS[error], { max: CUSTOM_PRINTOUT_NAME_MAX_LENGTH }) },
							performValidation: "onlyAfterInitialChange",
							showValidWhenValid: "never",
						}}
						reserveHeightForHelperText
					/>
				</Box>
			}
			confirm={t(mode === "create" ? "printout.custom.ai.save.new" : "printout.custom.ai.name.dialog.rename.confirm")}
			cancel={t("cancel")}
			confirmButtonVariant="primary"
			onConfirm={confirm}
			confirmDisabled={error !== null}
			confirmLoading={isSaving}
			maxWidth="xs"
			fullWidth
			dataTestId="custom-printout-name-dialog"
			confirmDataTestId="custom-printout-name-confirm"
			cancelDataTestId="custom-printout-name-cancel"
		/>
	);
};
